import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "393",
  "slug": "can-satellites-read-license-plates-from-space",
  "title": "Can a Satellite Really Read a License Plate From Space?",
  "excerpt": "Movies love the 'zoom and enhance' satellite shot. Here's what satellites can and can't actually see, and what those resolution numbers really mean.",
  "date": "2026-06-10",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1499669404910-ba8b35824a3c?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBjaXR5JTIwdmlld3xlbnwxfDB8fHwxNzgxMDc5MjE2fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "No. The sharpest commercial satellites resolve about 30 centimetres per pixel, enough to see a car as a blob, but nowhere near the few centimetres you would need to read its plate. The movie version is fiction.",
  "content": [
    {
      "type": "paragraph",
      "text": "It is one of cinema's favourite moments: an agent points at a grainy overhead image, barks 'enhance,' and a crisp license plate or a suspect's face snaps into focus, beamed live from a satellite. It makes for great tension. It is also almost entirely impossible. Understanding why is a quick lesson in what those satellite-resolution numbers really mean."
    },
    {
      "type": "heading",
      "text": "What Resolution Really Means"
    },
    {
      "type": "paragraph",
      "text": "Satellite resolution is usually given as a 'ground sample distance', the size of the area that a single pixel covers on the ground. A 30-centimetre resolution means each pixel represents a 30-by-30-centimetre square. To make out an object clearly, you generally need it to span several pixels. The characters on a license plate are only a few centimetres tall, far smaller than a single pixel from orbit, so they simply blur into nothing."
    },
    {
      "type": "heading",
      "text": "The Sharpest Eyes in Orbit"
    },
    {
      "type": "paragraph",
      "text": "The most capable commercial imaging satellites resolve detail at around 30 centimetres per pixel. That is genuinely impressive: from hundreds of kilometres up, you can see individual cars, the lines of a parking lot, swimming pools, shipping containers, and the shape of a rooftop. What you cannot see are faces, plates, or anything that depends on centimetre-scale detail. Higher-resolution sales have also historically been limited by regulation, so the public ceiling has stayed in that ballpark for years."
    },
    {
      "type": "heading",
      "text": "Why Not Just Build a Bigger Camera?"
    },
    {
      "type": "paragraph",
      "text": "Resolution is limited by physics, not just budget. Sharper images need a larger lens or mirror, which means heavier, far more expensive satellites that are harder to launch. On top of that, the atmosphere itself blurs the view: turbulent, shimmering air between the camera and the ground smears fine detail, the same effect that makes stars twinkle. Past a certain point, you spend enormous sums for tiny gains."
    },
    {
      "type": "heading",
      "text": "Satellite, Aircraft, or Drone?"
    },
    {
      "type": "paragraph",
      "text": "Here is a common source of confusion. The strikingly sharp 'satellite' images you scroll through in mapping apps are often not from satellites at all, but from aircraft flying far lower, sometimes capturing detail at 15 centimetres or better. Drones go sharper still, down to a centimetre or two, but only over a small local area. So when an image looks crisp enough to count roof tiles, it was probably taken from a plane, not from space."
    },
    {
      "type": "list",
      "items": [
        "Can see from orbit: building outlines, vehicles as small blocks, roads and runways, ships and their wakes, crop rows, large crowds as texture",
        "Cannot see from orbit: faces, license plates, text on signs, what someone is holding, anything only a few centimetres across"
      ]
    },
    {
      "type": "heading",
      "text": "The 'Enhance' Myth"
    },
    {
      "type": "paragraph",
      "text": "The deepest flaw in the movie trope is the idea that you can recover detail that was never captured. You cannot. If the information is smaller than a pixel, no amount of zooming or sharpening brings it back, it was never recorded in the first place. Software can sharpen edges or guess at likely shapes, but guessing is not the same as reading."
    }
  ]
};
